/**
 * Placement for the ticker-search results dropdown.
 *
 * The dropdown is a fixed-position portal, so nothing clips or repositions it
 * for free — anchored below a field that sits near the bottom of the viewport
 * (a panel's add-item footer, say) it would render mostly offscreen. Flip it
 * above when below can't hold it, and cap its height to whatever room the
 * chosen side actually has.
 */

/** Preferred height; the dropdown never grows past this even with room to spare. */
export const DROPDOWN_MAX_HEIGHT = 280
/** Below this, a downward dropdown is too stunted to be worth it — flip instead. */
export const DROPDOWN_MIN_HEIGHT = 160
/** Breathing room between the field and the dropdown, and against the viewport edge. */
const GAP = 4

export interface AnchorRect {
  top: number
  bottom: number
  left: number
}

export interface DropdownPosition {
  /** Set when opening downward — the dropdown's top edge. */
  top?: number
  /** Set when flipped up — distance from the viewport bottom to the dropdown's bottom edge. */
  bottom?: number
  left: number
  maxHeight: number
  /** True when the dropdown was flipped above the anchor. */
  flipped: boolean
}

export function computeDropdownPosition(
  rect: AnchorRect,
  viewportHeight: number,
): DropdownPosition {
  const spaceBelow = viewportHeight - rect.bottom - GAP
  const spaceAbove = rect.top - GAP

  // Only flip when below is genuinely too tight AND above is roomier —
  // otherwise the dropdown would hop sides on small viewport changes.
  const flipped = spaceBelow < DROPDOWN_MIN_HEIGHT && spaceAbove > spaceBelow

  const available = flipped ? spaceAbove : spaceBelow
  const maxHeight = Math.max(0, Math.min(DROPDOWN_MAX_HEIGHT, available))

  // Anchor the flipped dropdown by its BOTTOM edge, not its top. maxHeight is a
  // ceiling, not the rendered height — a two-result list is ~60px tall, so
  // positioning by `top = anchor - maxHeight` would float it 200px above the
  // field with a gaping hole underneath. Pinning the bottom keeps it welded to
  // the field whatever the result count.
  return flipped
    ? { bottom: viewportHeight - rect.top + GAP, left: rect.left, maxHeight, flipped }
    : { top: rect.bottom + GAP, left: rect.left, maxHeight, flipped }
}
